import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges } from '@angular/core';

import { BaseballScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { BaseballStatistics, EventScoreboard, OrderType, PairScore, SetGameScore } from '@frontend/sports/betting-offer/feature/model';
import { LiveSectionConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ValueSliderDirective } from '@frontend/sports/common/ui/value-slider';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { sumBy } from 'lodash-es';

@Component({
    selector: 'ms-baseball-scoreboard',
    templateUrl: 'baseball-scoreboard.html',
    styleUrls: ['./baseball-scoreboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [IconCustomComponent, ValueSliderDirective],
})
export class BaseballScoreboardComponent implements OnChanges {
    @Input() scoreboard: EventScoreboard;
    @Input() sportId: number;
    @HostBinding('class') className = 'baseball';

    private readonly baseballIcon = 'theme-baseball-indicator';
    homeTeamBase?: string = this.baseballIcon;
    awayTeamBase?: string = this.baseballIcon;
    isBaseVisible: boolean;

    @HostBinding('class.hidden')
    get hidden(): boolean {
        return !this.score || !this.score.participant1 || !this.score.participant2;
    }

    constructor(
        public sitecore: Sitecore,
        private liveSectionConfig: LiveSectionConfig,
        private baseballScoreboardFactory: BaseballScoreboardFactory,
    ) {}

    ngOnChanges(changes: ISimpleChanges<BaseballScoreboardComponent>): void {
        if (changes.scoreboard) {
            this.setBaseIcons();
        }
        this.isBaseVisible = this.homeTeamBase === this.baseballIcon || this.awayTeamBase === this.baseballIcon;
    }

    get score(): SetGameScore {
        return this.scoreboard.score as SetGameScore;
    }

    get points(): PairScore<number | string> | undefined {
        if (this.hidden) {
            return;
        }

        if (!this.score.participant1.points && !this.score.participant2.points) {
            return;
        }

        return {
            participant1: this.score.participant1.points as string,
            participant2: this.score.participant2.points as string,
        };
    }

    get games(): PairScore<number | string> | undefined {
        if (this.hidden) {
            return;
        }

        return {
            participant1: sumBy(this.score.participant1.sets, (set) => Number(set.points)),
            participant2: sumBy(this.score.participant2.sets, (set) => Number(set.points)),
        };
    }

    private setBaseIcons(): void {
        if (!this.liveSectionConfig.activeParticipantIconForSports.includes(this.sportId)) {
            return;
        }
        const baseballStats = this.scoreboard.statistics as BaseballStatistics;
        this.homeTeamBase = this.baseballScoreboardFactory.getBaseIcon(baseballStats, OrderType.Team01);
        this.awayTeamBase = this.baseballScoreboardFactory.getBaseIcon(baseballStats, OrderType.Team02);
    }
}
